import { createFileRoute } from "@tanstack/react-router";
import { I18nProvider } from "@/i18n/I18nProvider";
import { Nav } from "@/components/festival/Nav";
import { Footer } from "@/components/festival/Footer";
import { Button } from "@/components/ui/button";
import { openCookiePreferences } from "@/components/festival/CookieBanner";

export const Route = createFileRoute("/privacybeleid")({
  head: () => ({
    meta: [
      { title: "Privacybeleid — Openluchtfilmfestival Apeldoorn" },
      {
        name: "description",
        content:
          "Lees ons privacybeleid: welke persoonsgegevens we verwerken, met welk doel, op welke grondslag, hoe lang we ze bewaren en welke rechten je hebt.",
      },
      { property: "og:title", content: "Privacybeleid — Openluchtfilmfestival Apeldoorn" },
      {
        property: "og:description",
        content: "AVG-conform privacybeleid van het Openluchtfilmfestival Apeldoorn.",
      },
      { name: "robots", content: "index,follow" },
    ],
  }),
  component: PrivacyPage,
});

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-28 border-t border-border pt-8">
      <h2 className="font-display text-2xl font-medium text-foreground md:text-3xl">{title}</h2>
      <div className="mt-4 space-y-3 text-foreground/80 leading-relaxed">{children}</div>
    </section>
  );
}

function Placeholder({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-night-deep/60 px-1.5 py-0.5 font-mono-cinema text-[11px] text-gold">{children}</span>
  );
}

function PrivacyPage() {
  const lastUpdated = "17 juni 2026";
  return (
    <I18nProvider lang="nl">
      <div className="min-h-screen bg-background">
        <Nav />
        <main className="pt-28 pb-24 md:pt-36">
          <article className="mx-auto max-w-3xl px-5 md:px-8">
            <header className="mb-10">
              <p className="font-mono-cinema text-[10px] text-gold">— Juridisch</p>
              <h1 className="mt-3 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
                Privacy<span className="italic text-gold">beleid</span>
              </h1>
              <p className="mt-5 text-foreground/75">
                In dit privacybeleid leggen we uit welke persoonsgegevens wij verwerken, waarom, op welke grondslag en
                welke rechten je hebt. Dit beleid is opgesteld in overeenstemming met de Algemene Verordening
                Gegevensbescherming (AVG).
              </p>
              <p className="mt-2 text-sm text-foreground/60">Laatst bijgewerkt: {lastUpdated}</p>
            </header>

            <div className="space-y-10">
              <Section id="persoonsgegevens" title="1. Welke persoonsgegevens wij verwerken">
                <p>Afhankelijk van het contact dat je met ons hebt, kunnen wij de volgende gegevens verwerken:</p>
                <ul className="ml-5 list-disc space-y-1">
                  <li>Contactgegevens (naam, e-mailadres, telefoonnummer)</li>
                  <li>Aanmeldgegevens vrijwilligers (beschikbaarheid, voorkeursonderdelen)</li>
                  <li>Sponsoraanvragen (bedrijfsnaam, contactpersoon, budgetindicatie, bericht)</li>
                  <li>
                    Ticketinformatie via onze ticketpartner <Placeholder>ticketpartner</Placeholder>
                  </li>
                  <li>Technische gegevens (IP-adres, browsertype, bezochte pagina's) via noodzakelijke cookies</li>
                  <li>Statistiek- en marketinggegevens — uitsluitend met jouw toestemming</li>
                </ul>
              </Section>

              <Section id="doelen" title="2. Doelen van de verwerking">
                <p>Wij verwerken jouw gegevens uitsluitend voor de volgende doelen:</p>
                <ul className="ml-5 list-disc space-y-1">
                  <li>Het organiseren van het festival en bijbehorende communicatie</li>
                  <li>Het beantwoorden van vragen en het afhandelen van aanmeldingen</li>
                  <li>Coördinatie van vrijwilligers en partners</li>
                  <li>Beveiliging en goede werking van de website</li>
                  <li>Statistieken om de website te verbeteren (alleen met toestemming)</li>
                  <li>Eventuele nieuwsbrieven of marketing (alleen met toestemming)</li>
                </ul>
              </Section>

              <Section id="grondslagen" title="3. Grondslagen">
                <p>Voor iedere verwerking gebruiken wij een wettelijke grondslag uit de AVG:</p>
                <ul className="ml-5 list-disc space-y-1">
                  <li>
                    <strong>Toestemming</strong> — voor optionele cookies, nieuwsbrieven en marketing.
                  </li>
                  <li>
                    <strong>Uitvoering van een overeenkomst</strong> — bij ticketverkoop en samenwerking met
                    partners/vrijwilligers.
                  </li>
                  <li>
                    <strong>Gerechtvaardigd belang</strong> — voor beveiliging, fraudepreventie en het beantwoorden van
                    contactverzoeken.
                  </li>
                  <li>
                    <strong>Wettelijke verplichting</strong> — bijvoorbeeld voor administratie en belastingen.
                  </li>
                </ul>
              </Section>

              <Section id="bewaartermijnen" title="4. Bewaartermijnen">
                <p>We bewaren gegevens niet langer dan noodzakelijk:</p>
                <ul className="ml-5 list-disc space-y-1">
                  <li>
                    Contactverzoeken: <Placeholder>12 maanden</Placeholder>
                  </li>
                  <li>
                    Vrijwilligersgegevens: tot maximaal <Placeholder>2 jaar</Placeholder> na laatste editie
                  </li>
                  <li>Sponsorgegevens: gedurende de samenwerking + wettelijke bewaartermijn</li>
                  <li>Financiële/administratieve gegevens: 7 jaar (wettelijke verplichting)</li>
                  <li>Cookievoorkeuren: tot maximaal 12 maanden of tot je ze wijzigt</li>
                </ul>
              </Section>

              <Section id="derden" title="5. Derden en verwerkers">
                <p>
                  Wij delen jouw gegevens alleen met derden indien dat noodzakelijk is voor de uitvoering van onze
                  diensten of op basis van een wettelijke verplichting. Met verwerkers sluiten we een
                  verwerkersovereenkomst. Categorieën van ontvangers:
                </p>
                <ul className="ml-5 list-disc space-y-1">
                  <li>
                    Hosting & databaseplatform: <Placeholder>Delicious Media</Placeholder>
                  </li>
                  <li>
                    E-mailprovider: <Placeholder>info@deliciousmedia.nl</Placeholder>
                  </li>
                  <li>
                    Ticketpartner: <Placeholder>ticketpartner</Placeholder>
                  </li>
                  <li>
                    Statistieken (optioneel, op basis van toestemming): <Placeholder>google analytics</Placeholder>
                  </li>
                  <li>
                    Accountant / administratiekantoor: <Placeholder>Delicious Media</Placeholder>
                  </li>
                </ul>
                <p>
                  Doorgifte buiten de EER vindt alleen plaats met passende waarborgen, zoals de
                  EU-modelcontractbepalingen.
                </p>
              </Section>

              <Section id="rechten" title="6. Jouw rechten">
                <p>Onder de AVG heb je de volgende rechten:</p>
                <ul className="ml-5 list-disc space-y-1">
                  <li>Recht op inzage</li>
                  <li>Recht op rectificatie</li>
                  <li>Recht op verwijdering ("vergetelheid")</li>
                  <li>Recht op beperking van de verwerking</li>
                  <li>Recht op gegevensoverdraagbaarheid</li>
                  <li>Recht van bezwaar</li>
                  <li>Recht om eerder gegeven toestemming in te trekken</li>
                  <li>
                    Recht om een klacht in te dienen bij de Autoriteit Persoonsgegevens via{" "}
                    <a
                      href="https://autoriteitpersoonsgegevens.nl"
                      className="underline hover:text-gold"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      autoriteitpersoonsgegevens.nl
                    </a>
                  </li>
                </ul>
                <p>
                  Stuur een verzoek naar <Placeholder>info@openluchtfilmfestival.nl</Placeholder>. We reageren binnen 4
                  weken.
                </p>
              </Section>

              <Section id="cookies" title="7. Cookies">
                <p>
                  Wij plaatsen alleen noodzakelijke cookies zonder toestemming. Optionele cookies (statistieken,
                  marketing) worden uitsluitend geplaatst nadat je hiervoor expliciet toestemming hebt gegeven via onze
                  cookiebanner. Je keuze kun je altijd wijzigen:
                </p>
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-border bg-night-deep/60 hover:border-gold/50"
                    onClick={() => openCookiePreferences()}
                  >
                    Cookie-instellingen wijzigen
                  </Button>
                </div>
              </Section>

              <Section id="beveiliging" title="8. Beveiliging">
                <p>
                  Wij nemen passende technische en organisatorische maatregelen om jouw gegevens te beschermen tegen
                  verlies en onbevoegde toegang, waaronder versleutelde verbindingen (HTTPS), toegangsbeperking en
                  regelmatige beveiligingsupdates.
                </p>
              </Section>

              <Section id="wijzigingen" title="9. Wijzigingen">
                <p>
                  We kunnen dit privacybeleid van tijd tot tijd aanpassen. De meest actuele versie vind je altijd op
                  deze pagina.
                </p>
              </Section>

              <Section id="contact" title="10. Contact">
                <p>
                  Heb je vragen over dit privacybeleid of over de verwerking van jouw gegevens? Neem dan contact op via{" "}
                  <Placeholder>info@openluchtfilmfestival.nl</Placeholder> of per post via het adres bovenaan deze
                  pagina.
                </p>
              </Section>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </I18nProvider>
  );
}
